import { useEffect, useRef, useState } from "react";
import { init, type WidgetHandle } from "@nemo/widget";
import type { Api, Issue } from "../api.js";
import { ApiError } from "../api.js";

interface Props {
  api: Api;
  agentId: string;
  /** Changes after each successful save; the preview checks whether anything it shows has changed. */
  revision: number;
  /** True when nothing is waiting to be saved: the preview must show what the person typed. */
  settled: boolean;
  open: boolean;
  onClose: () => void;
}

/**
 * The real chat window talking to the real preview endpoint, so what you test is what visitors get.
 *
 * It must not flicker while someone edits the form, so:
 *  - the window is replaced only when the widget config it renders has actually changed (most edits, such as
 *    instructions, models, domains or knowledge, change nothing the window shows, and leave it alone);
 *  - when it does change, the new window is built off screen and swapped in one step, so there is no empty gap;
 *  - the conversation survives the swap (session and transcript are kept for this tab);
 *  - the status line has a fixed height, so the window never jumps when "Saving…" appears and disappears.
 */
export function Preview({ api, agentId, revision, settled, open, onClose }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const handle = useRef<WidgetHandle | null>(null);
  const shown = useRef<string | null>(null);
  const [missing, setMissing] = useState<Issue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  const drop = () => {
    handle.current?.destroy();
    handle.current = null;
    shown.current = null;
  };

  useEffect(() => {
    if (!open || !settled) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        // FAQs are searchable only once indexed. Doing it now (free when nothing changed) makes the preview
        // answer from exactly what is on screen, instead of only after publishing.
        setUpdating(true);
        try {
          await api.train(agentId);
        } catch {
          /* the preview still opens; it just may not know the newest FAQ */
        }
        if (cancelled) return;
        const res = await fetch(api.previewConfigUrl(agentId), { headers: api.authHeaders(), credentials: api.fetchCredentials() });
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        if (cancelled) return;
        setUpdating(false);
        if (res.status === 422) {
          drop();
          setMissing((body.issues as Issue[]) ?? []);
          setError(null);
          return;
        }
        if (!res.ok) throw new ApiError(res.status, String(body.error ?? "server"), "The preview could not load.");
        setMissing(null);
        setError(null);

        // The panel always shows the window itself; on the website it follows the mode chosen in Launch.
        const config = body as unknown as { appearance: Record<string, unknown> };
        const next = { ...config, appearance: { ...config.appearance, mode: "inline" } };
        const key = JSON.stringify(next);
        if (key === shown.current && handle.current) return; // nothing the window shows has changed

        const staging = document.createElement("div"); // build off screen, swap in one step
        const h = await init({
          agentId,
          apiBase: api.url(""),
          container: staging,
          config: next as never,
          headers: api.authHeaders(),
          credentials: api.fetchCredentials() === "include" ? "include" : undefined,
          chatUrl: api.previewChatUrl(agentId),
          persist: true, // a swap keeps the conversation going
        });
        if (cancelled || !host.current) return void h?.destroy();
        const el = staging.firstElementChild;
        if (!h || !el) return;
        handle.current?.destroy();
        handle.current = h;
        shown.current = key;
        host.current.replaceChildren(el);
      } catch (e) {
        if (!cancelled) {
          setUpdating(false);
          setError(e instanceof Error ? e.message : "The preview could not load.");
        }
      }
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, agentId, revision, settled, open]);

  // Closing the preview (the panel unmounts its window) or leaving the screen releases the window.
  useEffect(() => {
    if (!open) drop();
  }, [open]);
  useEffect(() => drop, []);

  if (!open) return null;
  const status = !settled ? "Saving your changes…" : updating ? "Updating the agent's knowledge…" : "";
  return (
    <aside className="preview" aria-label="Preview your agent">
      <div className="preview-head">
        <strong>Preview your agent</strong>
        <button className="btn ghost" onClick={onClose} aria-label="Close preview">
          Close
        </button>
      </div>
      <p className="hint">This is your saved draft talking for real. On your site it follows the display you chose in Launch.</p>
      {/* always present and always the same height, so the window below never moves */}
      <p className="hint preview-status" role="status">{status}</p>
      {missing && (
        <div className="issues">
          <strong>Complete these to start the preview:</strong>
          <ul>
            {missing.map((i, n) => (
              <li key={n}>{i.path}: {i.message}</li>
            ))}
          </ul>
        </div>
      )}
      {error && <div className="banner warn" role="alert">{error}</div>}
      <div className="preview-host" ref={host} hidden={!!missing} />
    </aside>
  );
}
